import { Controller } from '@hotwired/stimulus'
import { valueMatched } from '../_input_match_utils'
import { valuesToAdd, type ForceCase, type SplitSeparator } from './multi_text_utils'

type InputEventCustom = InputEvent & { target: HTMLInputElement }

const FOCUS_CLASSES = {
  wrapper: ['border-viaorange'],
  input: ['!text-800']
}

export default class extends Controller<HTMLDivElement> {
  static targets = ['wrapper', 'input', 'addButton', 'itemList', 'itemTemplate', 'noValuesText']

  declare readonly wrapperTarget: HTMLDivElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly addButtonTarget: HTMLButtonElement
  declare readonly itemListTarget: HTMLDivElement
  declare readonly itemTemplateTarget: HTMLTemplateElement
  declare readonly noValuesTextTarget: HTMLDivElement

  static values = {
    match: String,
    forceCase: String,
    defaultValue: Array<string>,
    splitValuesOn: Array<string>
  }

  declare matchValue: string
  declare readonly hasMatchValue: boolean
  declare forceCaseValue: string
  declare readonly hasForceCaseValue: boolean
  declare defaultValueValue: string[]
  declare readonly hasDefaultValueValue: boolean
  declare splitValuesOnValue: string[]
  declare readonly hasSplitValuesOnValue: boolean

  connect(): void {
    ;(this.defaultValueValue ?? []).forEach(this.#addItem.bind(this))

    this.#changeAddButtonState()
  }

  #changeAddButtonState(): void {
    const shouldBeDisabled = this.inputTarget.value === ''
    this.addButtonTarget.disabled = shouldBeDisabled
    this.addButtonTarget.classList.toggle('cursor-not-allowed', shouldBeDisabled)
    this.addButtonTarget.classList.toggle('opacity-50', shouldBeDisabled)
  }

  #addItem(value: string): void {
    const item = document.importNode(this.itemTemplateTarget.content, true)
    const itemText = item.querySelector('span')
    if (itemText === null) return
    itemText.textContent = value
    const itemValue = item.querySelector('input')
    if (itemValue === null) return
    itemValue.value = value

    this.itemListTarget.appendChild(item)

    this.noValuesTextTarget.classList.add('hidden')

    this.inputTarget.value = ''
    this.#changeAddButtonState()
  }

  inputFocus(): void {
    this.wrapperTarget.classList.add(...FOCUS_CLASSES.wrapper)
    this.inputTarget.classList.add(...FOCUS_CLASSES.input)
  }

  inputBlur(): void {
    this.wrapperTarget.classList.remove(...FOCUS_CLASSES.wrapper)
    this.inputTarget.classList.remove(...FOCUS_CLASSES.input)
  }

  inputInput(e: InputEventCustom | null, inputFromParameter?: HTMLInputElement): void {
    const input = e?.target ?? inputFromParameter
    if (typeof input === 'undefined') return

    if (!this.#isSplitModeEnabled()) {
      input.value = valueMatched(input.value, this.matchValue ?? null, null)
    }

    this.#changeAddButtonState()

    // force casing
    if (this.hasForceCaseValue) {
      const cursorPositionIndex = input.selectionStart ?? 0
      if (this.forceCaseValue === 'up') {
        input.value = input.value.toUpperCase()
      } else if (this.forceCaseValue === 'down') {
        input.value = input.value.toLowerCase()
      }
      input.setSelectionRange(cursorPositionIndex, cursorPositionIndex)
    }
  }

  inputKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault()
      this.clickAdd()
    }
  }

  clickAdd(): void {
    if (!this.#isSplitModeEnabled()) {
      const value = this.inputTarget.value
      if (value === '') return

      this.#addItem(value)
      return
    }

    const values = valuesToAdd(
      this.inputTarget.value,
      this.#splitSeparators(),
      this.matchValue ?? null,
      this.#forceCase()
    )
    if (values.length === 0) return

    values.forEach((value: string) => this.#addItem(value))
  }

  clickRemoveOnItem(e: MouseEvent): void {
    const item = e.currentTarget as HTMLElement | null
    if (item === null) return

    item.remove()

    if (this.itemListTarget.querySelectorAll('li')?.length === 0) {
      this.noValuesTextTarget.classList.remove('hidden')
    }
  }

  // We keep the current single-line input UX for now, so pasted newlines
  // are normalized to spaces instead of switching the component to a textarea.
  // In split mode, paste replaces the current staged input rather than merging
  // with it, so users do not end up with mixed batches using different separators.
  // Users still confirm the final tokens with Enter or the add button
  inputPaste(e: ClipboardEvent): void {
    if (!this.#isSplitModeEnabled()) return

    const pastedText = e.clipboardData?.getData('text') ?? ''
    if (pastedText === '') return

    e.preventDefault()

    this.inputTarget.value = pastedText.replace(/\r?\n/g, ' ')
    this.inputInput(null, this.inputTarget)
  }

  #isSplitModeEnabled(): boolean {
    return this.hasSplitValuesOnValue && this.splitValuesOnValue.length > 0
  }

  #forceCase(): ForceCase {
    if (!this.hasForceCaseValue) return null
    return this.forceCaseValue as ForceCase
  }

  #splitSeparators(): SplitSeparator[] {
    return this.#isSplitModeEnabled() ? (this.splitValuesOnValue as SplitSeparator[]) : []
  }
}
